/** Roster (screens §F1 Roster tab): people by team, Unassigned first; label and team selects; pre/post status; rotate CEO and CRO. */
import type { BoardTeamRow, RosterMember } from "@bsim/contracts";
import { TEAM_ROLES, UNIQUE_TEAM_ROLES } from "@bsim/contracts";
import type { ApiClient } from "../api/client";
import { useToast } from "../theme/Toast";
import { JoinCard } from "./JoinCard";
import { SeatPin } from "./SeatPin";
import { roleLabel } from "../shell/TopBar";
import { EmptyState } from "./EmptyState";
import { GxCard, GxSkeleton } from "../theme/primitives";
import { GxPill } from "../theme/GxPill";
import { humanRows } from "./derive";
import { useNow } from "../shell/useNow";
import { agoWords } from "./words";
import { useAct, type Act } from "./useAct";
import { useBoardData } from "./useBoardData";

type Role = (typeof TEAM_ROLES)[number];
const STATUS = { not_started: "Not started", in_progress: "In progress", submitted: "Submitted" } as const;
const statusTone = (s: RosterMember["pre"]) => (s === "submitted" ? "good" : s === "in_progress" ? "info" : "neutral");

function Person({ m, teams, act, cohortId, called }: { readonly called: boolean; readonly m: RosterMember; readonly teams: readonly BoardTeamRow[]; readonly act: Act; readonly cohortId: string }) {
  const now = useNow(30_000);
  const assign = (teamId: string | null, role: Role | null) => act("rosterAssign", { cohortId, userId: m.userId }, { teamId, role: teamId === null ? null : role });
  return (
    <li className="gx-fac-person" data-testid={`roster.${m.userId}`}>
      <span className="gx-fac-dot" role="img" data-online={m.online} aria-label={m.online ? "Online" : "Offline"} />
      <span className="gx-fac-who"><b>{m.displayName}</b></span>
      <span className="gx-small gx-faint" data-testid={`roster.presence.${m.userId}`}>{m.online ? "in the app now" : m.lastSeenAt === undefined ? "not in the app" : `not in the app · last seen ${agoWords(m.lastSeenAt, now)}`}</span>
      <span className="gx-small gx-faint gx-fac-email">{m.email ?? ""}</span>
      <select className="gx-fac-input gx-select" aria-label={`Team for ${m.displayName}`} value={m.teamId ?? ""} onChange={(e) => void assign(e.target.value === "" ? null : e.target.value, m.teamRole)}>
        <option value="">Unassigned</option>
        {teams.map((t) => <option key={t.teamId} value={t.teamId}>{t.name}</option>)}
      </select>
      <select className="gx-fac-input gx-select" aria-label={`Label for ${m.displayName}`} value={m.teamRole ?? ""} disabled={m.teamId === null} onChange={(e) => void assign(m.teamId, e.target.value === "" ? null : (e.target.value as Role))}>
        <option value="">No label</option>
        {TEAM_ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
      </select>
      <GxPill tone={called && m.pre === "not_started" ? "info" : statusTone(m.pre)}>Pre · {called && m.pre === "not_started" ? "Called in" : STATUS[m.pre]}</GxPill>
      <GxPill tone={statusTone(m.post)}>Post · {STATUS[m.post]}</GxPill>
    </li>
  );
}

/** Swap the two labels: CEO and CRO are unique per team, so the CEO steps aside while the CRO takes the seat. */
export async function rotate(cohortId: string, act: Act, ceo: RosterMember, cro: RosterMember, say: (line: string) => void): Promise<void> {
  const teamId = ceo.teamId;
  if (teamId === null) return;
  // The CEO steps aside first, so a failure at the second step leaves no CEO, at the third no CRO.
  const steps = [[ceo.userId, null, null], [cro.userId, "ceo", "CEO"], [ceo.userId, "cro", "risk officer"]] as const;
  for (const [userId, role, lacking] of steps) {
    if (!(await act("rosterAssign", { cohortId, userId }, { teamId, role })).ok) {
      if (lacking !== null) say(`The swap did not finish; the team has no ${lacking}. Assign one.`);
      return;
    }
  }
}

export function RosterPage({ cohortId, api }: { readonly cohortId: string; readonly api: ApiClient }) {
  const toast = useToast();
  const act = useAct(api);
  const { board, roster, calledIn } = useBoardData(cohortId);
  if (board === undefined) return <GxSkeleton height={240} />;
  const teams = humanRows(board.teams);
  const unassigned = roster.filter((m) => m.cohortRole === "participant" && m.teamId === null);
  return (
    <div className="gx-fac-page" data-testid="roster">
      <div className="gx-fac-head"><div><div className="gx-eyebrow">{board.cohort.name}</div><h1 className="gx-h1">Roster</h1></div></div>
      <JoinCard cohortId={cohortId} joinCode={board.cohort.joinCode} />
      {roster.length === 0 ? <EmptyState icon="roster" title="No one has joined yet">Share the join code above; people appear here as they arrive.</EmptyState> : null}
      {unassigned.length > 0 ? (
        <GxCard title="Unassigned" aside={<GxPill tone="warn">{unassigned.length}</GxPill>} className="gx-fac-rgroup" testId="roster.unassigned">
          <ul className="gx-fac-plist">{unassigned.map((m) => <Person key={m.userId} called={calledIn.includes(m.userId)} m={m} teams={teams} act={act} cohortId={cohortId} />)}</ul>
        </GxCard>
      ) : null}
      {teams.map((t) => {
        const people = roster.filter((m) => m.teamId === t.teamId);
        const ceo = people.find((m) => m.teamRole === "ceo");
        const cro = people.find((m) => m.teamRole === "cro");
        return (
          <GxCard key={t.teamId} title={t.name} className="gx-fac-rgroup" testId={`roster.team.${t.teamId}`}
            aside={ceo !== undefined && cro !== undefined
              ? <span className="gx-fac-rot"><span className="gx-small gx-faint">Swaps the CEO and risk officer labels between these two people.</span> <button type="button" className="gx-btn gx-btn--secondary" onClick={() => void rotate(cohortId, act, ceo, cro, toast)}>Rotate CEO and risk officer</button></span>
              : <GxPill tone="warn">{ceo === undefined && cro === undefined ? "No CEO or risk officer" : ceo === undefined ? "No CEO" : "No risk officer"}</GxPill>}>
            <ul className="gx-fac-plist">{people.map((m) => <Person key={m.userId} called={calledIn.includes(m.userId)} m={m} teams={teams} act={act} cohortId={cohortId} />)}</ul>
            <div className="gx-fac-qa">{UNIQUE_TEAM_ROLES.map((r) => <SeatPin key={r} cohortId={cohortId} team={t} role={r} act={act} />)}</div>
          </GxCard>
        );
      })}
    </div>
  );
}
